// /patient/meds — my medicines. Morning / evening list with a taken
// checkbox, add / delete, and a history section. Starts empty and is
// persisted via the mobile portal meds endpoints
// (`/api/patient-mobile/v1/meds/`).
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BASE_URLS } from "../../components/env";
import { getSupabase } from "../../components/supabaseClient";
import MedsHistory, {
  dayKey,
  todayKey,
  prettyTime,
  lastDose,
  describeDose,
} from "./MedsHistory";
import "../css/PatientPortal.css";
import {
  loadMedsCacheSync,
  readMedsCache,
  writeMedsCache,
  readAutoScanStampEver,
  writeAutoScanStampEver,
  localDateKey,
} from "../../utils/medsCache";

function getBackendBaseUrl() {
  if (typeof window === "undefined") return BASE_URLS.render.backend;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1")
    return BASE_URLS.local.backend;
  return (
    (process.env.REACT_APP_BACKEND_URL &&
      process.env.REACT_APP_BACKEND_URL.trim() !== "" &&
      process.env.REACT_APP_BACKEND_URL) ||
    BASE_URLS.render.backend
  );
}

const MAX_SCAN_BYTES = 10 * 1024 * 1024;

// Map a raw med from the API into the shape this page uses. Keeps
// `started_at` / `ended_at` / `doses_log` so <MedsHistory> can rebuild the
// calendar and the change timeline without another round trip. Also
// preserves the `added_by_*` / `discontinued_by_*` editor fields so the
// history view can render "Added by Dr. Jane Lee" / "Stopped by patient".
function mapMed(m) {
  const log = Array.isArray(m.doses_log) ? m.doses_log : [];
  // Recover today's dose time from the log so "Taken at 8:04 AM" survives a
  // page reload — previously it only existed in local state until refresh.
  const todaysDose = log.find(
    (d) => d && dayKey(d.date) === todayKey() && d.taken !== false,
  );
  return {
    id: m.id,
    name: m.name,
    dose: m.dose || "",
    reason: m.reason || "",
    slot:
      m.frequency === "evening" || m.schedule === "evening"
        ? "evening"
        : "morning",
    taken: Boolean(m.taken_today),
    takenAt: todaysDose ? prettyTime(todaysDose.taken_at) : undefined,
    dosesLog: log,
    startedAt: m.started_at || null,
    endedAt: m.ended_at || null,
    prescriber: m.prescriber || "",
    notes: m.notes || "",
    addedBy: pickEditor(m, "added_by"),
    discontinuedBy: pickEditor(m, "discontinued_by"),
  };
}

function pickEditor(med, prefix) {
  if (!med || typeof med !== "object") return null;
  const userId = med[`${prefix}_user_id`];
  const name = med[`${prefix}_name`];
  const email = med[`${prefix}_email`];
  const role = med[`${prefix}_role`];
  const kind = med[`${prefix}_kind`];
  const at = med[`${prefix}_at`];
  if (!userId && !name && !email && !kind) return null;
  return {
    userId: userId || null,
    name: name || null,
    email: email || null,
    role: role || null,
    kind: kind || null,
    at: at || null,
  };
}

// Loose key used to spot the same medicine written two different ways
// ("Lisinopril 20mg tablet" vs "lisinopril 20 mg") when merging the results of
// a multi-visit scan into the list the patient already has. Digit↔letter
// boundaries get a separator so the common "20mg" / "20 mg" shorthand-vs-
// spelled-out variants collapse together — mirrors the backend's
// `_normalize_med_key` in `patient_meds_scan.py`.
function normalizeMedName(name) {
  const s = String(name || "").toLowerCase();
  return s
    .replace(/([a-z])([0-9])/g, "$1 $2")
    .replace(/([0-9])([a-z])/g, "$1 $2")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

async function getAccessToken() {
  try {
    const supabase = await getSupabase();
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    return data && data.session ? data.session.access_token : null;
  } catch (err) {
    console.warn("PatientMeds: getAccessToken failed", err);
    return null;
  }
}

export async function medsApi(path, method, body) {
  const token = await getAccessToken();
  if (!token) {
    // The meds API authenticates by bearer token only, so without a session the
    // request can only come back 401. Fail here instead of sending it; callers
    // already show err.message to the patient.
    const err = new Error("You're signed out. Please sign in again to see your medicines.");
    err.status = 401;
    err.payload = { error: "unauthenticated" };
    throw err;
  }
  const headers = { "Content-Type": "application/json" };
  headers.Authorization = `Bearer ${token}`;
  const res = await fetch(
    `${getBackendBaseUrl()}/api/patient-mobile/v1/meds${path}`,
    {
      method,
      headers,
      credentials: "include",
      body: body ? JSON.stringify(body) : undefined,
    },
  );
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(
      (payload && (payload.error || payload.detail)) ||
        `Request failed (${res.status}).`,
    );
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

export default function PatientMeds({ embedded = false } = {}) {
  // Synchronous cache hydration so the very first paint can already show
  // the list (no skeleton flash on repeat visits). The shape inside the
  // cache is already `mapMed`'d, so we hand it straight to useState.
  const cacheSeed = loadMedsCacheSync();
  const [meds, setMeds] = useState(() =>
    cacheSeed && Array.isArray(cacheSeed.current) ? cacheSeed.current : []
  );
  const [history, setHistory] = useState(() =>
    cacheSeed && Array.isArray(cacheSeed.history) ? cacheSeed.history : []
  );
  const [loading, setLoading] = useState(!cacheSeed);
  const [loadError, setLoadError] = useState(null);
  // Flips to true after the very first mount effect completes — gates
  // the auto-scan so it can't fire before we know whether the cache
  // (or a fresh server response) already populated the list.
  const [bootstrapped, setBootstrapped] = useState(false);
  // Subtle indicator for the silent background refresh — only shown
  // while the network call is in flight after we've already painted
  // from cache, so the user knows the list might still be reconciling.
  const [syncing, setSyncing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [draft, setDraft] = useState({
    name: "",
    dose: "",
    reason: "",
    slot: "morning",
  });
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState(null);
  const [docScanning, setDocScanning] = useState(false);
  const fileInputRef = useRef(null);
  // Guard so the auto-scan below only fires once per mount — repeated
  // `refreshMeds` calls (after toggles, deletes, etc.) shouldn't kick off
  // a second LLM round trip. Backend dedupes against the persisted list,
  // so even a repeat scan wouldn't add duplicates; this just saves a bill.
  const autoScanTriggeredRef = useRef(false);

  // `silent` reconciles are skipped entirely when there's no cache yet —
  // they're reserved for "we already have something on screen" so the
  // background fetch never overwrites a failed first-load error with a
  // blank list. Every successful GET persists the result back to
  // localStorage so the next page open hydrates instantly.
  const refreshMeds = async ({ silent = false } = {}) => {
    const hadCache = Boolean(readMedsCache());
    if (!silent && !hadCache) setLoadError(null);
    setSyncing(true);
    try {
      const data = await medsApi("/", "GET");
      const shape = (data && data.meds) || {};
      const current = Array.isArray(shape.current) ? shape.current : [];
      const past = Array.isArray(shape.history) ? shape.history : [];
      const mappedCurrent = current.map(mapMed);
      const mappedHistory = past.map((m) => ({
        ...mapMed(m),
        removedAt: m.ended_at || null,
      }));
      setMeds(mappedCurrent);
      setHistory(mappedHistory);
      writeMedsCache({ current: mappedCurrent, history: mappedHistory });
      return true;
    } catch (err) {
      // On a re-sync, DON'T wipe the list — the optimistic update is more
      // recent than this transient GET failure, and clearing state is what
      // made meds "pop up again" the next time the request succeeded.
      // Only the very first load (silent === false AND no cache fallback)
      // treats errors as fatal.
      if (!silent && !hadCache) {
        setLoadError(err.message || "Failed to load medicines");
        setMeds([]);
        setHistory([]);
      }
      return false;
    } finally {
      if (!silent) setLoading(false);
      setSyncing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Re-hydrate from cache using the same key used in the useState
      // seed (loadMedsCacheSync is sync, so the first paint already had
      // the cached data). This branch only matters if a different tab
      // wrote to it between the seed read and now — rare, but cheap.
      const cached = readMedsCache();
      if (cached && cached.meds) {
        if (!cancelled) {
          setMeds(
            Array.isArray(cached.meds.current) ? cached.meds.current : []
          );
          setHistory(
            Array.isArray(cached.meds.history) ? cached.meds.history : []
          );
        }
      }
      // Reconcile with the server. `silent: true` keeps `loading`
      // untouched when we already painted from cache, so the skeleton
      // never replaces the cached list mid-flight.
      const ok = await refreshMeds({ silent: true });
      if (!cancelled) {
        if (!ok && !cached) {
          setLoadError("Couldn't load your medicines. Please try again.");
        }
        setLoading(false);
        setBootstrapped(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const taken = meds.filter((m) => m.taken).length;
  const total = meds.length;
  const eveningDue = meds.filter(
    (m) => m.slot === "evening" && !m.taken,
  ).length;

  const toggleTaken = async (id) => {
    const med = meds.find((m) => m.id === id);
    if (!med) return;
    const undo = Boolean(med.taken);
    const prevLog = Array.isArray(med.dosesLog) ? med.dosesLog : [];
    const nowIso = new Date().toISOString();
    const today = todayKey();
    // Mirror what the server does to `doses_log` so the history calendar
    // reflects the tick straight away instead of waiting for a refresh.
    const nextLog = undo
      ? prevLog.filter((d) => !(d && dayKey(d.date) === today))
      : [...prevLog, { date: today, taken_at: nowIso, taken: true }];
    setMeds((cur) =>
      cur.map((m) =>
        m.id === id
          ? {
              ...m,
              taken: !undo,
              takenAt: !undo ? prettyTime(nowIso) : undefined,
              dosesLog: nextLog,
            }
          : m,
      ),
    );
    try {
      await medsApi(
        `/${encodeURIComponent(id)}/taken/`,
        "POST",
        undo ? { undo: true } : {},
      );
      // Reconcile with the server: `taken_today` is the source of truth
      // (see docs/patient-meds-api.md). Without this, anything that later
      // triggers refreshMeds — a remount, the scan-visits flow, etc. —
      // could revert the checkmark to the server's pre-toggle value.
      await refreshMeds({ silent: true });
    } catch (err) {
      setScanStatus({
        type: "error",
        text: `Couldn't save dose to server: ${err.message}`,
      });
      setMeds((cur) =>
        cur.map((m) =>
          m.id === id
            ? {
                ...m,
                taken: undo,
                takenAt: undo ? m.takenAt : undefined,
                dosesLog: prevLog,
              }
            : m,
        ),
      );
    }
  };

  const removeMed = async (id) => {
    const med = meds.find((m) => m.id === id);
    if (!med) return;
    // Optimistic update: drop the med from the active list and push it
    // into history (with `endedAt` set) so the calendar reflects that the
    // med was on the list up through today.
    const nowIso = new Date().toISOString();
    setHistory((cur) => [
      { ...med, endedAt: nowIso, removedAt: nowIso },
      ...cur,
    ]);
    setMeds((cur) => cur.filter((m) => m.id !== id));
    try {
      await medsApi(`/${encodeURIComponent(id)}/`, "DELETE");
      // Reconcile with the server instead of patching from a partial
      // response — `meds.current` / `meds.history` returned by the DELETE
      // handler can race with the optimistic update and leave the med
      // half-removed. A silent GET gives us the authoritative shape.
      const ok = await refreshMeds({ silent: true });
      if (!ok) {
        // GET failed but the DELETE went through — leave the optimistic
        // state alone (it's the freshest truth we have) and surface a
        // gentle notice so the patient knows the page may be slightly
        // behind the server.
        setScanStatus({
          type: "info",
          text: "Saved, but couldn't refresh your list. Try opening this page again in a moment.",
        });
      }
    } catch (err) {
      // DELETE itself failed — revert the optimistic removal so the med
      // stays visible and the patient can retry.
      setHistory((cur) => cur.filter((m) => m.id !== id));
      setMeds((cur) => {
        const next = [...cur];
        const idx = next.findIndex((m) => m.id === id);
        if (idx >= 0) return next;
        next.push({ ...med, taken: false, takenAt: undefined });
        return next;
      });
      setScanStatus({
        type: "error",
        text: `Couldn't remove that medicine: ${err.message}`,
      });
    }
  };

  const addMed = async (e) => {
    e.preventDefault();
    const name = draft.name.trim();
    if (!name || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await medsApi("/", "POST", {
        name,
        dose: draft.dose.trim(),
        frequency: draft.slot,
        reason: draft.reason.trim(),
      });
      // Refresh from the server so the new med's `id`, `started_at`,
      // `taken_today` (false) and `doses_log` are exactly what the DB
      // stored. Reading only `data.meds.current` from the POST response
      // was inconsistent with the rest of the page (history stayed stale,
      // other meds could be out of order).
      await refreshMeds({ silent: true });
      setDraft({ name: "", dose: "", reason: "", slot: "morning" });
      setShowForm(false);
    } catch (err) {
      setSubmitError(err.message || "Failed to add medication");
    } finally {
      setSubmitting(false);
    }
  };

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error("read failed"));
      reader.readAsDataURL(file);
    });

  const handleScanFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setScanStatus({
        type: "error",
        text: "Please choose an image file (JPG, PNG, or HEIC).",
      });
      return;
    }
    if (file.size > MAX_SCAN_BYTES) {
      setScanStatus({
        type: "error",
        text: `Image is too large (max ${MAX_SCAN_BYTES / (1024 * 1024)} MB).`,
      });
      return;
    }

    setScanning(true);
    setScanStatus({ type: "info", text: "Scanning your photo…" });

    try {
      const dataUrl = await fileToDataUrl(file);
      const res = await fetch(
        `${getBackendBaseUrl()}/api/patient/meds/scan-image/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ image: dataUrl, mime: file.type }),
        },
      );
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload.status === "unavailable") {
        setScanStatus({
          type: "info",
          text:
            "Scanning is not available right now. Please add your medicines manually.",
        });
        return;
      }
      if (!res.ok) {
        const msg =
          (payload && (payload.error || payload.detail)) ||
          `Scan failed (${res.status}).`;
        setScanStatus({ type: "error", text: msg });
        return;
      }
      const found = Array.isArray(payload.medications)
        ? payload.medications
        : [];
      if (found.length === 0) {
        setScanStatus({
          type: "info",
          text:
            "No medicines could be read from that photo. Try a clearer shot or add them manually.",
        });
        return;
      }

      const incoming = found.map((m) => ({
        name: m.name,
        dose: (m.dose || "").trim(),
        frequency: m.slot === "evening" ? "evening" : "morning",
        reason: (m.reason || "").trim(),
      }));

      const added = [];
      for (const med of incoming) {
        if (!med.name) continue;
        try {
          const data = await medsApi("/", "POST", med);
          const created = data && data.medication;
          if (created) added.push(created);
        } catch (err) {
          console.warn("PatientMeds: failed to persist scanned med", err);
        }
      }
      await refreshMeds();
      setScanStatus({
        type: added.length > 0 ? "success" : "error",
        text:
          added.length > 0
            ? `Added ${added.length} medicine${added.length === 1 ? "" : "s"} from your photo. Review the list below.`
            : "Could not save the medicines read from your photo. Please add them manually.",
      });
    } catch (err) {
      setScanStatus({
        type: "error",
        text: `Could not scan that image: ${err && err.message ? err.message : "unknown error"}.`,
      });
    } finally {
      setScanning(false);
    }
  };

  const triggerScan = () => {
    if (scanning) return;
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleScanAllVisits = async () => {
    if (docScanning) return;
    const token = await getAccessToken();
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    setDocScanning(true);
    setScanStatus({ type: "info", text: "Reading all of your past visits…" });
    try {
      const res = await fetch(
        `${getBackendBaseUrl()}/api/patient/meds/scan-latest-document/`,
        {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify({}),
        },
      );
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          (payload && (payload.error || payload.detail)) ||
          `Scan failed (${res.status}).`;
        setScanStatus({ type: "error", text: msg });
        return;
      }
      // Stamp the "ever scanned" flag for any clean HTTP response so
      // future page opens don't re-fire the LLM. We still let the
      // backend decide what (if anything) to add; the stamp just means
      // "we asked and got an answer".
      writeAutoScanStampEver();
      const status = payload.status;
      const scanned = Number(payload.visits_scanned) || 0;
      const foundVisits = Number(payload.visits_found) || 0;
      // "Scanned 4 of 5 visits" — the gap is visits with no document yet.
      const scanSummary =
        scanned > 0
          ? `Scanned ${scanned} visit${scanned === 1 ? "" : "s"}` +
            (foundVisits > scanned ? ` (of ${foundVisits} on file)` : "") +
            "."
          : "";

      if (status === "unavailable") {
        setScanStatus({
          type: "info",
          text:
            payload.error ||
            "Scanning from your visit notes is not available right now. " +
              "Please add your medicines manually.",
        });
        return;
      }
      if (status === "no_document") {
        setScanStatus({
          type: "info",
          text:
            payload.error ||
            "We couldn't find a visit note to read for you yet.",
        });
        return;
      }
      // Backend already dedupes against the persisted list (same
      // `_normalize_med_key` strategy the doctor modal uses), so a second
      // press of this button — or the auto-scan that fires on page mount —
      // never piles up duplicates. `payload.medications` is the list that
      // was actually added; `payload.skipped` is the count that was
      // already there.
      const added = Array.isArray(payload.medications) ? payload.medications : [];
      const skipped = Number(payload.skipped) || 0;
      if (added.length === 0 && skipped === 0) {
        setScanStatus({
          type: "info",
          text:
            `${scanSummary} No medicines were mentioned in them. ` +
            "Add them manually if your doctor prescribed something new.",
        });
        return;
      }
      // Re-read the list so the UI matches what just got persisted.
      await refreshMeds({ silent: true });
      if (added.length === 0) {
        setScanStatus({
          type: "info",
          text:
            `${scanSummary} Found ${skipped} medicine${skipped === 1 ? "" : "s"}, ` +
            "all of which were already on your list.",
        });
        return;
      }
      const skippedNote =
        skipped > 0
          ? ` ${skipped} ${skipped === 1 ? "was" : "were"} already on your list.`
          : "";
      setScanStatus({
        type: "success",
        text:
          `${scanSummary} Added ${added.length} medicine${added.length === 1 ? "" : "s"}.` +
          skippedNote +
          " Review the list below.",
      });
    } catch (err) {
      setScanStatus({
        type: "error",
        text: `Could not read your visits just now: ${err && err.message ? err.message : "unknown error"}.`,
      });
    } finally {
      setDocScanning(false);
    }
  };

  // Auto-scan on first load: if the patient has no meds yet, kick off the
  // visit scan without waiting for them to click "Add meds from visits".
  // Gated by `bootstrapped` (we can't decide until we know whether the
  // cache / first server fetch populated the list) AND by a persistent
  // `ever` stamp in localStorage, so the scan fires at most ONCE per
  // device — reloading, opening in a new tab, or coming back tomorrow
  // all skip the scan and render from cache. The user still has the
  // manual "Add meds from visits" button to re-scan on demand; the
  // backend dedupes against the persisted list so the cost of a
  // duplicate is just an LLM round-trip.
  useEffect(() => {
    if (!bootstrapped) return;
    if (autoScanTriggeredRef.current) return;
    autoScanTriggeredRef.current = true;
    if (readAutoScanStampEver()) return;
    if (meds.length > 0) {
      // Already have meds — record the stamp so we never auto-scan again.
      writeAutoScanStampEver();
      return;
    }
    handleScanAllVisits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapped, meds.length]);

  const morning = meds.filter((m) => m.slot === "morning");
  const evening = meds.filter((m) => m.slot === "evening");

  const MedRow = ({ m, removable, showCheck }) => {
    const previous = m.taken ? null : lastDose(m);
    return (
      <div className={`pp-med-row ${m.taken ? "is-taken" : ""}`}>
        {showCheck && (
          <button
            type="button"
            className={`pp-med-check ${m.taken ? "is-on" : ""}`}
            onClick={() => toggleTaken(m.id)}
            aria-label={m.taken ? "Mark as not taken" : "Mark as taken"}
            title={m.taken ? "Mark as not taken" : "Mark as taken"}
          >
            {m.taken ? "✓" : ""}
          </button>
        )}
        <div className="pp-med-body">
          <div className="pp-med-name">{m.name}</div>
          <div className="pp-med-dose">
            {m.dose} · {m.reason}
          </div>
          {m.taken && m.takenAt && (
            <div className="pp-med-taken">Taken at {m.takenAt}</div>
          )}
          {previous && (
            <div className="pp-med-last">
              Last taken {describeDose(previous)}
            </div>
          )}
        </div>
        {removable && (
          <button
            type="button"
            className="pp-med-remove"
            onClick={() => removeMed(m.id)}
            aria-label={`Remove ${m.name}`}
            title="Remove medication"
          >
            ✕
          </button>
        )}
      </div>
    );
  };

  const allTaken = total > 0 && taken === total;

  // While the first fetch is in flight we deliberately DON'T render the real
  // list: `meds` is still `[]`, so the empty-state copy ("No morning
  // medicines", "0 of 0 taken") would render as fact and then be replaced a
  // moment later. A skeleton with the same shape avoids both the false
  // message and the layout jump.
  if (loading) {
    return (
      <div className="pp-portal">
        {!embedded && (
          <div className="pp-page-head">
            <Link to="." className="pp-back-link">
              ‹ Home
            </Link>
            <h1 className="pp-page-title">My medicines</h1>
            <div className="pp-page-sub">Loading your medicines…</div>
          </div>
        )}
        {embedded && (
          <div className="px-3 pt-3 text-sm text-slate-500 dark:text-slate-400">
            Loading your medicines…
          </div>
        )}

        <div className="pp-card" aria-busy="true" aria-live="polite">
          <span className="pp-sr-only">Loading your medicines…</span>
          <div className="pp-meds-progress">
            <div>
              <span className="pp-skel pp-skel-line" style={{ width: 110 }} />
              <div className="pp-meds-bar" style={{ marginTop: 8 }} />
            </div>
            <span className="pp-skel pp-skel-pill" />
          </div>

          {["This morning", "Tonight"].map((label) => (
            <div className="pp-meds-slot" key={label}>
              <div className="pp-meds-slot-h">{label}</div>
              {[0, 1].map((i) => (
                <div className="pp-med-row" key={i}>
                  <span className="pp-skel pp-skel-check" />
                  <div className="pp-med-body">
                    <span
                      className="pp-skel pp-skel-line"
                      style={{ width: `${58 + i * 14}%` }}
                    />
                    <span
                      className="pp-skel pp-skel-line pp-skel-sm"
                      style={{ width: `${34 + i * 10}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="pp-portal">
      {!embedded && (
        <div className="pp-page-head">
          <Link to="." className="pp-back-link">
            ‹ Home
          </Link>
          <h1 className="pp-page-title">My medicines</h1>
          <div className="pp-page-sub">
            {taken} of {total} taken today
            {syncing && (
              <span
                className="pp-page-sub-sync"
                aria-live="polite"
                style={{
                  marginLeft: 8,
                  opacity: 0.7,
                  fontSize: "0.85em",
                }}
              >
                · refreshing…
              </span>
            )}
          </div>
        </div>
      )}
      {embedded && (
        <div className="px-3 pt-3">
          <div className="pp-page-sub">
            {taken} of {total} taken today
          </div>
        </div>
      )}

      <div className="pp-card">
        {loadError && (
          <div className="pp-saved-flash pp-error" style={{ marginBottom: 10 }}>
            {loadError}
          </div>
        )}
        <div className="pp-meds-progress">
          <div>
            <div className="pp-visit-eyebrow" style={{ marginBottom: 4 }}>
              {taken} of {total} taken
            </div>
            <div className="pp-meds-bar">
              <div
                className="pp-meds-bar-fill"
                style={{ width: `${total ? (taken / total) * 100 : 0}%` }}
              />
            </div>
          </div>
          {allTaken ? (
            <span className="pp-row-pill pp-green">✓ Great job this week!</span>
          ) : (
            <span className="pp-row-pill">In progress</span>
          )}
        </div>
        <p className="pp-detail-p">
          Tap the circle next to a medicine once you've taken it. Add new
          medicines as they are prescribed, and remove ones you no longer take —
          removed medicines are kept in your history below.
        </p>

        <div className="pp-meds-slot">
          <div className="pp-meds-slot-h">This morning</div>
          {morning.length === 0 ? (
            <div className="pp-detail-p">No morning medicines.</div>
          ) : (
            morning.map((m) => <MedRow key={m.id} m={m} removable showCheck />)
          )}
        </div>

        <div className="pp-meds-slot">
          <div className="pp-meds-slot-h">Tonight</div>
          {evening.length === 0 ? (
            <div className="pp-detail-p">No evening medicines.</div>
          ) : (
            evening.map((m) => <MedRow key={m.id} m={m} removable showCheck />)
          )}
        </div>

        <div
          style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}
        >
          <button
            type="button"
            className="pp-btn pp-ghost"
            onClick={() => setShowForm((v) => !v)}
          >
            ➕ {showForm ? "Cancel" : "Add a medication"}
          </button>
          <button
            type="button"
            className="pp-btn pp-ghost"
            onClick={triggerScan}
            disabled={scanning || docScanning}
            title="Take a photo of your pill bottles and let the assistant read them"
          >
            {scanning ? "Scanning…" : "📷 Scan medicines from a photo"}
          </button>
          <button
            type="button"
            className="pp-btn pp-ghost"
            onClick={handleScanAllVisits}
            disabled={scanning || docScanning}
            title="Read all of your past visits and add any medicines your doctors mentioned"
          >
            {docScanning ? "Reading visits…" : "📄 Add meds from visits"}
          </button>
          <Link to="wizard" className="pp-btn pp-ghost">
            ✨ Ask the helper about my medicines
          </Link>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleScanFile}
            style={{ display: "none" }}
            aria-hidden="true"
            tabIndex={-1}
          />
        </div>

        {scanStatus && (
          <div
            className={
              scanStatus.type === "error"
                ? "pp-saved-flash pp-error"
                : scanStatus.type === "success"
                  ? "pp-saved-flash"
                  : "pp-saved-flash"
            }
            style={{ marginTop: 10 }}
            role="status"
          >
            {scanStatus.text}
          </div>
        )}

        {showForm && (
          <form
            className="pp-vitals-form"
            onSubmit={addMed}
            style={{ marginTop: 14 }}
          >
            <div className="pp-input">
              <label className="pp-input-lbl" htmlFor="med-name">
                Name
              </label>
              <input
                id="med-name"
                type="text"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. Lisinopril 20 mg"
                required
              />
            </div>
            <div className="pp-input">
              <label className="pp-input-lbl" htmlFor="med-dose">
                Dose
              </label>
              <input
                id="med-dose"
                type="text"
                value={draft.dose}
                onChange={(e) => setDraft({ ...draft, dose: e.target.value })}
                placeholder="e.g. 1 tablet"
              />
            </div>
            <div className="pp-input">
              <label className="pp-input-lbl" htmlFor="med-reason">
                Reason
              </label>
              <input
                id="med-reason"
                type="text"
                value={draft.reason}
                onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
                placeholder="e.g. for blood pressure"
              />
            </div>
            <div className="pp-input">
              <label className="pp-input-lbl">When</label>
              <fieldset className="pp-radio-group" style={{ margin: 0 }}>
                <label className="pp-radio">
                  <input
                    type="radio"
                    name="slot"
                    checked={draft.slot === "morning"}
                    onChange={() => setDraft({ ...draft, slot: "morning" })}
                  />
                  <span>Morning</span>
                </label>
                <label className="pp-radio">
                  <input
                    type="radio"
                    name="slot"
                    checked={draft.slot === "evening"}
                    onChange={() => setDraft({ ...draft, slot: "evening" })}
                  />
                  <span>Evening</span>
                </label>
              </fieldset>
            </div>
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
              <button
                type="submit"
                className="pp-btn pp-teal"
                disabled={submitting}
              >
                {submitting ? "Saving…" : "Save medication"}
              </button>
              <button
                type="button"
                className="pp-btn pp-ghost"
                onClick={() => setShowForm(false)}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
            {submitError && (
              <div
                className="pp-saved-flash pp-error"
                style={{ gridColumn: "1 / -1" }}
                role="status"
              >
                {submitError}
              </div>
            )}
          </form>
        )}

        {eveningDue > 0 && (
          <div className="pp-saved-flash" style={{ marginTop: 12 }}>
            {eveningDue} evening dose{eveningDue === 1 ? "" : "s"} scheduled
          </div>
        )}
      </div>

      <MedsHistory meds={meds} history={history} />
    </div>
  );
}
